<script setup lang="ts">
import { PhCaretLeft, PhCaretRight, PhDotsThree, PhPlus } from '@phosphor-icons/vue'
import { computed, onBeforeUnmount, onMounted, shallowRef, watch } from 'vue'
import { buildImageResizeUrl } from '@/utils/distribute/media-url'
import type { Emits, Props } from './type'

defineOptions({
  name: 'FollowCartoonStrip',
})

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  followLoadingIds: () => [],
  layout: 'scroll',
  showAllAction: true,
})
const emit = defineEmits<Emits>()

const rootElement = shallowRef<HTMLElement | null>(null)
const stripElement = shallowRef<HTMLElement | null>(null)
const skeletonItems = computed(() => Array.from({ length: 8 }, (_, index) => index))
const isGridLayout = computed(() => props.layout === 'grid')
const followedCount = computed(() => props.cartoons.filter((item) => item.hasFollow === 1).length)
const shouldShowAllAction = computed(() => props.showAllAction && followedCount.value >= 10)
const showChrome = shallowRef(false)
const canScroll = shallowRef(false)
const canScrollLeft = shallowRef(false)
const canScrollRight = shallowRef(false)
let resizeObserver: ResizeObserver | null = null

function resolveAvatarSrc(src: string | null | undefined) {
  return buildImageResizeUrl(src, { width: 264, height: 264, mode: 'fill' }) ?? src ?? ''
}

function isFollowLoading(id: string) {
  return props.followLoadingIds.includes(id)
}

function syncScrollState() {
  const element = stripElement.value

  if (!element) {
    canScroll.value = false
    canScrollLeft.value = false
    canScrollRight.value = false
    return
  }

  const maxScrollLeft = Math.max(0, element.scrollWidth - element.clientWidth)
  const scrollLeft = Math.max(0, element.scrollLeft)

  canScroll.value = maxScrollLeft > 2
  canScrollLeft.value = scrollLeft > 2
  canScrollRight.value = scrollLeft < maxScrollLeft - 2
}

function scrollByDirection(direction: -1 | 1) {
  stripElement.value?.scrollBy({
    left: direction * 420,
    behavior: 'smooth',
  })

  requestAnimationFrame(syncScrollState)
}

function setupResizeObserver() {
  resizeObserver?.disconnect()
  resizeObserver = null

  syncScrollState()

  if (!rootElement.value || !stripElement.value || typeof ResizeObserver === 'undefined') {
    return
  }

  resizeObserver = new ResizeObserver(() => {
    syncScrollState()
  })
  resizeObserver.observe(rootElement.value)
  resizeObserver.observe(stripElement.value)
}

onMounted(() => {
  setupResizeObserver()
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})

watch(
  () => [props.loading, props.cartoons.length],
  () => {
    requestAnimationFrame(setupResizeObserver)
  },
)
</script>

<template>
  <section
    ref="rootElement"
    class="follow-strip"
    :class="{ 'is-grid': isGridLayout }"
    aria-label="关注角色列表"
    @pointerenter="showChrome = true"
    @pointerleave="showChrome = false"
    @focusin="showChrome = true"
    @focusout="showChrome = false"
  >
    <div v-if="canScroll && !isGridLayout" class="follow-strip__fade is-left" aria-hidden="true" />
    <div v-if="canScroll && !isGridLayout" class="follow-strip__fade is-right" aria-hidden="true" />

    <button
      v-if="canScroll && !isGridLayout"
      type="button"
      class="follow-strip__nav is-left"
      :class="{ 'is-visible': showChrome, 'is-disabled': !canScrollLeft }"
      aria-label="向左查看关注角色"
      :disabled="!canScrollLeft"
      @click="scrollByDirection(-1)"
    >
      <PhCaretLeft :size="18" weight="bold" />
    </button>

    <button
      v-if="canScroll && !isGridLayout"
      type="button"
      class="follow-strip__nav is-right"
      :class="{ 'is-visible': showChrome, 'is-disabled': !canScrollRight }"
      aria-label="向右查看关注角色"
      :disabled="!canScrollRight"
      @click="scrollByDirection(1)"
    >
      <PhCaretRight :size="18" weight="bold" />
    </button>

    <div ref="stripElement" class="follow-strip__scroller" :class="{ 'is-grid': isGridLayout }" @scroll="syncScrollState">
      <template v-if="loading && cartoons.length === 0">
        <div
          v-for="item in skeletonItems"
          :key="item"
          class="follow-strip__entry is-skeleton"
          aria-hidden="true"
        >
          <div class="follow-strip__item is-skeleton">
            <div class="follow-strip__avatar-skeleton">
              <span class="follow-strip__follow-skeleton" />
            </div>
            <span class="follow-strip__label-skeleton" />
          </div>
        </div>
      </template>

      <template v-else>
        <div
          v-for="cartoon in cartoons"
          :key="cartoon.id"
          class="follow-strip__entry"
        >
          <button
            type="button"
            class="follow-strip__item"
            :title="cartoon.name"
            @click="emit('cartoonClick', cartoon)"
          >
            <span class="follow-strip__avatar">
              <img
                :src="resolveAvatarSrc(cartoon.avatar)"
                :alt="cartoon.name"
                loading="lazy"
                decoding="async"
              />
            </span>
            <span class="follow-strip__label">{{ cartoon.name }}</span>
          </button>

          <button
            v-if="cartoon.hasFollow !== 1"
            type="button"
            class="follow-strip__follow"
            :disabled="isFollowLoading(cartoon.id)"
            :aria-label="`关注${cartoon.name}`"
            @click.stop="emit('followClick', cartoon)"
          >
            <PhPlus :size="13" weight="bold" />
          </button>
        </div>

        <button
          v-if="shouldShowAllAction"
          type="button"
          class="follow-strip__item is-all"
          title="我的关注"
          @click="emit('allClick')"
        >
          <span class="follow-strip__avatar is-all">
            <PhDotsThree :size="22" weight="bold" />
          </span>
          <span class="follow-strip__label is-muted">我的关注</span>
        </button>
      </template>
    </div>
  </section>
</template>

<style scoped>
.follow-strip {
  position: relative;
  width: 100%;
  max-width: 1440px;
  margin: 0;
  margin-inline: auto;
  padding: 8px 0;
  --follow-strip-mask-base: var(--app-bg);
  --follow-strip-nav-bg: var(--app-surface-raised);
  --follow-strip-nav-fg: var(--app-text-soft);
  --follow-strip-avatar-surface: var(--app-avatar-surface);
  --follow-strip-all-surface: var(--app-avatar-surface-muted);
  --follow-strip-text: var(--app-text-main);
  --follow-strip-muted: var(--app-text-muted);
  --follow-strip-avatar-shadow: var(--app-shadow-soft);
  --follow-strip-nav-shadow: var(--app-shadow-floating);
}

.follow-strip__scroller {
  display: flex;
  gap: 0;
  overflow-x: auto;
  padding: 0 12px;
  scroll-behavior: smooth;
  scrollbar-width: none;
}

.follow-strip__scroller.is-grid {
  width: min(700px, 100%);
  flex-wrap: wrap;
  justify-content: center;
  gap: 24px;
  margin: 0 auto;
  padding: 12px;
  overflow: visible;
}

.follow-strip__scroller::-webkit-scrollbar {
  display: none;
}

.follow-strip__item {
  display: inline-flex;
  min-width: 0;
  flex: 0 0 auto;
  min-height: 135px;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 7px;
  padding: 12px;
  border: 0;
  background: transparent;
  color: var(--follow-strip-text);
  cursor: pointer;
  transform-origin: center;
  vertical-align: top;
  transition:
    transform var(--app-interaction-duration) var(--app-interaction-ease),
    opacity 0.18s ease;
}

.follow-strip__entry {
  position: relative;
  flex: 0 0 auto;
}

.follow-strip__scroller.is-grid .follow-strip__entry {
  width: 112px;
}

.follow-strip__item:hover,
.follow-strip__item:focus-visible {
  transform: translateY(-2px) scale(1.015);
}

.follow-strip__item:active {
  transform: scale(var(--app-interaction-object-scale-active-subtle));
}

.follow-strip__avatar {
  position: relative;
  display: grid;
  width: 88px;
  height: 88px;
  place-items: center;
  overflow: hidden;
  border-radius: 999px;
  background: var(--follow-strip-avatar-surface);
  box-shadow:
    inset 0 0 0 1px var(--app-avatar-ring),
    var(--follow-strip-avatar-shadow);
}

.follow-strip__avatar img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.follow-strip__avatar.is-all {
  background: var(--follow-strip-all-surface);
  color: var(--follow-strip-muted);
}

.follow-strip__follow {
  position: absolute;
  right: 18px;
  top: 76px;
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--app-avatar-ring);
  border-radius: 999px;
  background: var(--app-positive);
  color: var(--app-positive-foreground);
  box-shadow: var(--app-positive-shadow);
  transition:
    transform var(--app-interaction-duration) var(--app-interaction-ease),
    opacity 0.16s ease;
}

.follow-strip__follow:hover,
.follow-strip__follow:focus-visible {
  transform: scale(var(--app-interaction-object-scale-hover));
}

.follow-strip__follow:active {
  transform: scale(var(--app-interaction-object-scale-active));
}

.follow-strip__follow:disabled {
  opacity: 0.62;
}

@media (prefers-reduced-motion: reduce) {
  .follow-strip__item,
  .follow-strip__follow {
    transition: opacity 0.18s ease;
  }

  .follow-strip__item:hover,
  .follow-strip__item:focus-visible,
  .follow-strip__item:active,
  .follow-strip__follow:hover,
  .follow-strip__follow:focus-visible,
  .follow-strip__follow:active {
    transform: none;
  }
}

.follow-strip__label {
  max-width: 88px;
  overflow: hidden;
  color: var(--follow-strip-text);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.follow-strip__label.is-muted {
  color: var(--follow-strip-muted);
}

.follow-strip__fade {
  position: absolute;
  top: 8px;
  height: 112px;
  z-index: 1;
  width: 52px;
  pointer-events: none;
}

.follow-strip__fade.is-left {
  left: 0;
  background: linear-gradient(90deg, var(--follow-strip-mask-base) 0%, rgb(255 255 255 / 0) 100%);
}

.follow-strip__fade.is-right {
  right: 0;
  background: linear-gradient(270deg, var(--follow-strip-mask-base) 0%, rgb(255 255 255 / 0) 100%);
}

.follow-strip__nav {
  position: absolute;
  top: 64px;
  z-index: 2;
  display: inline-flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 999px;
  background: var(--follow-strip-nav-bg);
  box-shadow: var(--follow-strip-nav-shadow);
  color: var(--follow-strip-nav-fg);
  opacity: 0;
  pointer-events: none;
  transform: translateY(-50%);
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.follow-strip__nav.is-visible {
  opacity: 1;
  pointer-events: auto;
}

.follow-strip__nav.is-disabled {
  opacity: 0;
  pointer-events: none;
}

.follow-strip__nav.is-left {
  left: 6px;
}

.follow-strip__nav.is-right {
  right: 6px;
}

.follow-strip__item.is-skeleton {
  cursor: default;
}

.follow-strip__avatar-skeleton,
.follow-strip__label-skeleton {
  position: relative;
  overflow: hidden;
  background: var(--app-skeleton-base);
}

.follow-strip__avatar-skeleton::before,
.follow-strip__label-skeleton::before {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent 0%, var(--app-skeleton-shimmer) 48%, transparent 100%);
  animation: follow-strip-shimmer 1.4s linear infinite;
  content: '';
}

.follow-strip__avatar-skeleton {
  position: relative;
  width: 88px;
  height: 88px;
  border-radius: 999px;
}

.follow-strip__follow-skeleton {
  position: absolute;
  right: 6px;
  bottom: 6px;
  width: 24px;
  height: 24px;
  border: 2px solid var(--app-avatar-ring);
  border-radius: 999px;
  background: var(--app-skeleton-line);
}

.follow-strip__label-skeleton {
  width: 60px;
  height: 16px;
  border-radius: 999px;
}

@keyframes follow-strip-shimmer {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(100%);
  }
}

@media (max-width: 640px) {
  .follow-strip {
    margin-bottom: 0;
  }

  .follow-strip__scroller {
    padding: 0 6px;
  }

  .follow-strip__scroller.is-grid {
    width: 100%;
    gap: 16px;
    padding: 12px 8px;
  }

  .follow-strip__scroller.is-grid .follow-strip__entry {
    width: 96px;
  }

  .follow-strip__item {
    min-height: 113px;
    padding: 10px 8px;
  }

  .follow-strip__avatar,
  .follow-strip__avatar-skeleton {
    width: 72px;
    height: 72px;
  }

  .follow-strip__label {
    max-width: 72px;
  }

  .follow-strip__label-skeleton {
    height: 16px;
  }

  .follow-strip__nav,
  .follow-strip__fade {
    display: none;
  }
}
</style>
